import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setLiveImageCropZoom } from "../../../redux/actions/LiveImageAction";
import {
  getShowCrossHair,
  getShowRuler,
} from "../../../redux/reducers/LiveImageReducer";
import { getModuleState } from "../../../redux/reducers/ModuleStateReducer";
import {
  getBestFit,
  getCursorOffset,
} from "../../../redux/reducers/PsrCameraLiveImageReducer";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { setCursorOffset } from "../../../redux/actions/PsrCameraLiveImageAction";

type Props = {
  psrIdentifier: string;
  psrCameraId: number;
  style?: CSSProperties;
};

function PsrCameraLiveImage({ psrIdentifier, psrCameraId, style = {} }: Props) {
  const dispatch = useDispatch();
  const cursorOffset = useTypedSelector((state) => getCursorOffset(state));

  const [boundingRect, setBoundingRect] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  const psrState = useTypedSelector((state) =>
    getModuleState(state, psrIdentifier)
  ).psr;

  const psrCameraState = psrState?.psrCameras.find(
    (x) => x.psrCameraId === psrCameraId
  );

  const originalImageWidth = psrCameraState?.imageWidth ?? 100;
  const originalImageHeight = psrCameraState?.imageHeight ?? 100;
  const bestFit = useTypedSelector((state) => getBestFit(state));

  // const isLiveStreamEnabled = imagerState?.freerunCapture;
  // const manualControlModeEnabled =
  //   imagerState?.state === EImagingModuleStatus.Imageable;

  const fovX = psrCameraState?.fovX ?? 1;
  const fovY = psrCameraState?.fovX ?? 1;
  const rulerLength = (fovX / originalImageWidth) * 82;

  const imageStreamRef = useRef<HTMLImageElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const showRuler = useTypedSelector((state) => getShowRuler(state));
  const showCroshair = useTypedSelector((state) => getShowCrossHair(state));

  // Create state variables for the position and size of the rectangle
  const [rectX, setRectX] = useState(50);
  const [rectY, setRectY] = useState(50);
  const rectWidth = 161;
  const rectHeight = 161;
  const EPSILON = 0.1;

  // Create state variables for the mouse position and dragging status
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (canvas != null) {
      const x =
        (cursorOffset.x / (psrState?.psrCameras[0]?.fovX ?? 0)) *
          canvas.width *
          1000 +
        canvas.width / 2 -
        rectWidth / 2;
      const y =
        (cursorOffset.y / (psrState?.psrCameras[0]?.fovY ?? 0)) *
          canvas.height *
          1000 +
        canvas.height / 2 -
        rectHeight / 2;
      const dx = Math.abs(rectX - x);
      const dy = Math.abs(rectY - y);

      //console.log("STATE", { x: x, y: y });
      if (dx > EPSILON) {
        setRectX(x);
      }
      if (dy > EPSILON) {
        setRectY(y);
      }
    }
  }, [cursorOffset.x, cursorOffset.y, canvasRef.current]);

  // Define a function to draw the rectangle on canvas
  const drawRect = (ctx: CanvasRenderingContext2D) => {
    ctx.fillStyle = "#E38006";
    ctx.fillRect(rectX, rectY + rectHeight / 2 - 2, rectWidth, 3);
    ctx.fillRect(rectX + rectWidth / 2 - 2, rectY, 3, rectHeight);
  };

  // Define a function to clear the canvas
  const clearCanvas = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  };

  // Define a function to check if the mouse is inside the rectangle
  const isInsideRect = (x: number, y: number) => {
    return (
      x >= rectX &&
      x <= rectX + rectWidth &&
      y >= rectY &&
      y <= rectY + rectHeight
    );
  };

  // Define a function to handle mouse down event
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Get the mouse position relative to the canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Set the mouse position state
      setMouseX(x);
      setMouseY(y);

      // Check if the mouse is inside the rectangle
      if (isInsideRect(x, y)) {
        // Set the dragging status to true
        setIsDragging(true);
      }
    }
  };

  // Define a function to handle mouse move event
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Get the mouse position relative to the canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Check if the dragging status is true
      if (isDragging) {
        // Calculate the difference between the current and previous mouse positions
        const dx = x - mouseX;
        const dy = y - mouseY;

        // Update the rectangle position state
        setRectX(rectX + dx);
        setRectY(rectY + dy);

        // Update the mouse position state
        setMouseX(x);
        setMouseY(y);
      }
    }
  };

  // Define a function to handle mouse up event
  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Set the dragging status to false
    setIsDragging(false);
    const canvas = canvasRef.current;
    if (canvas == null) {
      return;
    }
    const offsetX =
      ((rectX - canvas.width / 2 + rectWidth / 2) *
        (psrState?.psrCameras[0]?.fovX ?? 0)) /
      canvas.width /
      1000;
    const offsetY =
      ((rectY - canvas.height / 2 + rectHeight / 2) *
        (psrState?.psrCameras[0]?.fovY ?? 0)) /
      canvas.height /
      1000;
    console.log("STATE", {
      width: canvas.width,
      height: canvas.height,
      fovX: psrState?.psrCameras[0]?.fovX,
      fovY: psrState?.psrCameras[0]?.fovY,
      offsetX: offsetX,
      offsetY: offsetY,
    });

    dispatch(setCursorOffset({ x: offsetX, y: offsetY }));
  };

  useEffect(() => {
    // Get the canvas context
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        // Clear the canvas before drawing
        clearCanvas(ctx);

        // Draw the rectangle on canvas
        drawRect(ctx);
      }
    }
  });

  useEffect(() => {
    const imageStream = imageStreamRef?.current;
    const display = displayRef?.current;
    const overlay = overlayRef?.current;
    const canvas = canvasRef?.current;
    if (
      imageStream == null ||
      display == null ||
      overlay == null ||
      canvas == null
    ) {
      return;
    }

    const displayWidth = display.clientWidth;
    const displayHeight = display.clientHeight;

    if (bestFit) {
      const ratio = Math.min(
        displayWidth / originalImageWidth,
        displayHeight / originalImageHeight
      );
      const scaledWidth = originalImageWidth * ratio;
      const scaledHeight = originalImageHeight * ratio;

      //------WIDTH & HEIGHT-------//
      imageStream.width = scaledWidth;
      imageStream.height = scaledHeight;
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;
      overlay.style.width = scaledWidth + "px";
      overlay.style.height = scaledHeight + "px";

      //-------LEFT && RIGHT-------//
      const imageLeft = (displayWidth - scaledWidth) / 2;
      const imageTop = (displayHeight - scaledHeight) / 2;

      imageStream.style.left = imageLeft + "px";
      imageStream.style.top = imageTop + "px";
      canvas.style.left = imageLeft + "px";
      canvas.style.top = imageTop + "px";
      overlay.style.left = imageLeft + "px";
      overlay.style.top = imageTop + "px";
      const boundingRect = canvas.getBoundingClientRect();
      setBoundingRect(boundingRect);
      dispatch(setLiveImageCropZoom(1, 1));

      setRectX((scaledWidth - rectWidth) / 2);
      setRectY((scaledHeight - rectHeight) / 2);

      // const ctx = canvas.getContext("2d");
      // if (ctx != null) {
      //   drawRect(ctx);
      // }
    } else {
      //------WIDTH & HEIGHT-------//
      const canvasWidth = Math.min(displayWidth, originalImageWidth);
      const canvasHeight = Math.min(displayHeight, originalImageHeight);
      imageStream.width = originalImageWidth;
      imageStream.height = originalImageHeight;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      overlay.style.width = canvasWidth + "px";
      overlay.style.height = canvasHeight + "px";

      //-------LEFT && RIGHT-------//
      const imageLeft = (displayWidth - originalImageWidth) / 2;
      const imageTop = (displayHeight - originalImageHeight) / 2;
      const canvasLeft = imageLeft < 0 ? 0 : imageLeft;
      const canvasTop = imageTop < 0 ? 0 : imageTop;
      imageStream.style.left = imageLeft + "px";
      imageStream.style.top = imageTop + "px";
      canvas.style.left = canvasLeft + "px";
      canvas.style.top = canvasTop + "px";
      overlay.style.left = canvasLeft + "px";
      overlay.style.top = canvasTop + "px";

      const boundingRect = canvas.getBoundingClientRect();
      setBoundingRect(boundingRect);
      dispatch(
        setLiveImageCropZoom(
          originalImageWidth / canvasWidth,
          originalImageHeight / canvasHeight
        )
      );

      setRectX((canvasWidth - rectWidth) / 2);
      setRectY((canvasHeight - rectHeight) / 2);
    }
  }, [
    originalImageHeight,
    originalImageWidth,
    imageStreamRef?.current?.naturalWidth,
    imageStreamRef?.current?.naturalHeight,
    bestFit,
  ]);

  const src =
    "//" + window.location.hostname + ":" + psrCameraState?.streamServerPort;

  console.log("LIVE IMAGE RENDERED");

  return (
    <div
      ref={displayRef}
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      <div
        ref={overlayRef}
        style={{
          position: "absolute",
          zIndex: 10,
        }}
      >
        {/* -------Crosshair------- */}
        {showCroshair && (
          <svg
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
            }}
            viewBox="0 0 1001 1001"
          >
            <line
              x1="0"
              y1="500"
              x2="1000"
              y2="500"
              stroke="red"
              strokeWidth="2"
            />
            <line
              x1="500"
              y1="0"
              x2="500"
              y2="1000"
              stroke="red"
              strokeWidth="2"
            />
          </svg>
        )}
        {/* -------Ruler------- */}
        {/* {showRuler && (
          <div
            style={{
              backgroundColor: "gray",
              position: "absolute",
              left: 12,
              bottom: 37,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              paddingRight: 11,
            }}
          >
            <IconDrag />
            <IconRuler
              style={{
                marginLeft: 12,
                marginRight: 9,
              }}
            />
            <div>{rulerLength.toFixed(0) + "µM"}</div>
          </div>
        )} */}
      </div>

      <img
        style={{
          position: "absolute",
        }}
        ref={imageStreamRef}
        src={src}
        alt="..."
      />
      <canvas
        id="CANVAS"
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        // onDoubleClick={onDoubleClick}
        style={{
          position: "absolute",
          zIndex: 10,
          userSelect: "none",
        }}
      />
    </div>
  );
}

function areEqual(prevProps: Props, nextProps: Props) {
  return (
    prevProps.psrIdentifier === nextProps.psrIdentifier &&
    prevProps.psrCameraId === nextProps.psrCameraId
  );
}

export default React.memo(PsrCameraLiveImage, areEqual);
